<script setup lang="ts">
import { SIDEBAR_WIDTH_MOBILE, useSidebar } from './utils'
import type { SidebarProps } from '.'

defineOptions({
	inheritAttrs: false,
})

const props = withDefaults(defineProps<SidebarProps>(), {
	side: 'left',
	variant: 'sidebar',
	collapsible: 'offcanvas',
})

const { isMobile, state, openMobile, setOpenMobile } = useSidebar()
</script>

<template>
	<div
		v-if="collapsible === 'none'"
		:class="cn('flex h-full w-[var(--sidebar-width)] flex-col bg-sidebar text-sidebar-foreground', props.class)"
		v-bind="$attrs"
	>
		<slot />
	</div>

	<ui-sheet
		v-else-if="isMobile"
		:open="openMobile"
		v-bind="$attrs"
		@update:open="setOpenMobile"
	>
		<ui-sheet-content
			data-sidebar="sidebar"
			data-mobile="true"
			:side="side"
			class="bg-sidebar text-sidebar-foreground w-[var(--sidebar-width)] p-0 [&>button]:hidden"
			:style="{
				'--sidebar-width': SIDEBAR_WIDTH_MOBILE,
			}"
		>
			<div class="flex size-full flex-col">
				<slot />
			</div>
		</ui-sheet-content>
	</ui-sheet>

	<div
		v-else
		class="group peer hidden md:block"
		:data-state="state"
		:data-collapsible="state === 'collapsed' ? collapsible : ''"
		:data-variant="variant"
		:data-side="side"
	>
		<!-- This is what handles the sidebar gap on desktop  -->
		<div
			:class="cn(
				'duration-200 relative h-svh w-[var(--sidebar-width)] bg-transparent transition-[width] ease-linear',
				'group-data-[collapsible=offcanvas]:w-0',
				'group-data-[side=right]:rotate-180',
				variant === 'floating' || variant === 'inset'
					? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
					: 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]',
			)"
		/>
		<div
			:class="cn(
				'duration-200 fixed inset-y-0 z-10 hidden h-svh w-[var(--sidebar-width)] transition-[left,right,width] ease-linear md:flex',
				side === 'left'
					? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
					: 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
				// Adjust the padding for floating and inset variants.
				variant === 'floating' || variant === 'inset'
					? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]'
					: 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l',
				props.class,
			)"
			v-bind="$attrs"
		>
			<div
				data-sidebar="sidebar"
				class="text-sidebar-foreground bg-sidebar group-data-[variant=floating]:border-sidebar-border flex size-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow"
			>
				<slot />
			</div>
		</div>
	</div>
</template>
